/**
 * usePermissions Hook
 *
 * Provides permission checks for investment operations.
 * Uses the DecisionMaker role to control access to restricted status changes.
 */

import { isRestrictedStatus } from '@medsuite/models';

import { useAuth } from '../providers/AuthProvider';

/**
 * Role constants
 */
export const DECISION_MAKER_ROLE = 'DecisionMaker';
export const ADMINISTRATOR_ROLE = 'Administrator';

/**
 * Permissions hook return type
 */
export interface PermissionsResult {
  /**
   * Whether the current user has DecisionMaker role
   */
  isDecisionMaker: boolean;

  /**
   * Whether the current user has Administrator role
   */
  isAdministrator: boolean;

  /**
   * Whether the user can manage investment statuses
   * (has either DecisionMaker or Administrator role)
   */
  canManageStatus: boolean;

  /**
   * Check if the user can change to a specific status
   *
   * Restricted statuses (approved, deferred, rejected, cancelled)
   * require DecisionMaker or Administrator role.
   */
  canChangeToStatus: (status: string) => boolean;

  /**
   * Check if the user can change from a specific status
   *
   * Changing FROM restricted statuses (approved, deferred, rejected, cancelled)
   * requires DecisionMaker or Administrator role.
   */
  canChangeFromStatus: (currentStatus: string) => boolean;

  /**
   * Check if the user can make a status transition
   *
   * Combines canChangeFromStatus and canChangeToStatus checks.
   */
  canMakeStatusTransition: (currentStatus: string, targetStatus: string) => boolean;

  /**
   * All roles the current user has
   */
  roles: string[];
}

/**
 * Hook for checking user permissions
 *
 * @returns Permission check functions and role information
 *
 * @example
 * ```tsx
 * function StatusSelector() {
 *   const { canChangeToStatus, isDecisionMaker } = usePermissions();
 *
 *   return (
 *     <select>
 *       {statuses.map(status => (
 *         <option
 *           key={status}
 *           value={status}
 *           disabled={!canChangeToStatus(status)}
 *         >
 *           {status}
 *         </option>
 *       ))}
 *     </select>
 *   );
 * }
 * ```
 */
export function usePermissions(): PermissionsResult {
  const { roles, hasRole } = useAuth();

  const isDecisionMaker = hasRole(DECISION_MAKER_ROLE);
  const isAdministrator = hasRole(ADMINISTRATOR_ROLE);
  const canManageStatus = isDecisionMaker || isAdministrator;

  /**
   * Check if user can change to a specific status
   *
   * Non-restricted statuses: anyone can change
   * Restricted statuses: requires DecisionMaker or Administrator role
   */
  const canChangeToStatus = (status: string): boolean => {
    if (!isRestrictedStatus(status)) {
      return true;
    }
    return canManageStatus;
  };

  /**
   * Check if user can change from a specific status
   *
   * Non-restricted statuses: anyone can change from them
   * Restricted statuses: requires DecisionMaker or Administrator role to change from them
   */
  const canChangeFromStatus = (currentStatus: string): boolean => {
    if (!isRestrictedStatus(currentStatus)) {
      return true;
    }
    return canManageStatus;
  };

  /**
   * Check if user can make a status transition
   *
   * Combines both canChangeFromStatus and canChangeToStatus checks.
   */
  const canMakeStatusTransition = (currentStatus: string, targetStatus: string): boolean => {
    return canChangeFromStatus(currentStatus) && canChangeToStatus(targetStatus);
  };

  return {
    isDecisionMaker,
    isAdministrator,
    canManageStatus,
    canChangeToStatus,
    canChangeFromStatus,
    canMakeStatusTransition,
    roles,
  };
}

export default usePermissions;
